// DataGrid public types. Feature set modelled on MUI X Data Grid (community + a few pro features), implemented
// from scratch on Radix + Tailwind — no MUI dependency.
import type { ReactNode } from "react";

export type GridColType = "string" | "number" | "date" | "dateTime" | "boolean" | "singleSelect" | "actions";
export type GridAlign = "left" | "right" | "center";
export type GridSortDirection = "asc" | "desc";
export type GridDensity = "compact" | "standard" | "comfortable";
export type GridRowId = string;

export interface GridValueOption {
  value: string;
  label: string;
}

export interface GridColDef<R> {
  /** Unique key; the default value is `row[field]`. */
  field: string;
  headerName: string;
  type?: GridColType;
  /** Fixed width in px. Columns without a width share the remaining space (like MUI `flex`). */
  width?: number;
  minWidth?: number;
  align?: GridAlign;
  /** Header tooltip. */
  description?: string;
  sortable?: boolean;
  filterable?: boolean;
  hideable?: boolean;
  resizable?: boolean;
  /** Hidden until the user shows it in the columns panel. */
  hiddenByDefault?: boolean;
  /** Restrict the filter operators offered for this column (e.g. what a server supports). */
  filterOperators?: string[];
  /** For `singleSelect`. */
  valueOptions?: GridValueOption[];
  valueGetter?: (row: R) => unknown;
  /** Text used for display fallback, quick filter and CSV. */
  valueFormatter?: (value: unknown, row: R) => string;
  renderCell?: (params: { row: R; value: unknown; formatted: string }) => ReactNode;
  sortComparator?: (a: unknown, b: unknown) => number;
  /** Excluded from CSV export (e.g. action buttons). */
  disableExport?: boolean;
  cellClassName?: string;
}

export interface GridSortItem {
  field: string;
  sort: GridSortDirection;
}
export type GridSortModel = GridSortItem[];

export interface GridFilterItem {
  id: string;
  field: string;
  operator: string;
  value?: unknown;
}

export interface GridFilterModel {
  items: GridFilterItem[];
  logicOperator: "and" | "or";
  quickFilterValues: string[];
}

export interface GridPaginationModel {
  page: number;
  pageSize: number;
}

export type GridMode = "client" | "server";

/** Everything the user can rearrange; persisted per grid `id`. */
export interface GridColumnState {
  order: string[];
  hidden: string[];
  widths: Record<string, number>;
  pinned: { left: string[]; right: string[] };
}

export const EMPTY_FILTER_MODEL: GridFilterModel = { items: [], logicOperator: "and", quickFilterValues: [] };
